<template>
    <section class="section create-bracket-page">
        <div class="container-fluid">
            <div class="columns">
                <div class="column">
                    <h1 class="title">{{ translate("admin_create_bracket.title") }}</h1>
                </div>
            </div>

            <div class="columns">
                <div class="column is-6">
                    <div class="field">
                        <label class="label">{{ translate("common.category") }}:</label>
                        <div class="control">
                            <BracketCategorySelect v-model="selectedComplexBracket" :items="complexBracketsListing" />
                        </div>
                    </div>
                    <div class="field">
                        <label class="label">{{ translate("admin_create_bracket.bracket_type") }}:</label>
                        <div class="control">
                            <div class="select is-fullwidth">
                                <select v-model="selectedSupportedBracketType" @change="onChangeBracketType">
                                    <option value="0">---</option>
                                    <option
                                        v-for="supportedBracketType in supportedBracketTypes"
                                        :value="supportedBracketType.id"
                                        :key="supportedBracketType.id"
                                    >
                                        {{ translate(supportedBracketType.key) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <BracketSeedingSelector v-model="selectedSeedingType" />

                    <div class="buttons">
                        <LoadingButton :is-loading="generatingInProgress" class="button is-primary" @click="generateBracketSeeding">
                            {{ translate("admin_create_bracket.generate") }}
                        </LoadingButton>
                        <LoadingButton
                            :is-loading="savingInProgress"
                            class="button is-success"
                            @click="saveBracket"
                            :disabled="!generatedBracket.length"
                        >
                            {{ translate("common.save") }}
                        </LoadingButton>
                        <button :disabled="!generatedBracket.length" class="button" @click="openAcademiesSummary">
                            {{ translate("admin_create_bracket.show_academies") }}
                        </button>

                        <BracketColorOptions
                            :button-disabled="!generatedBracket.length"
                            @clearColoring="onClearColoring"
                            @coloringChoice="onColoringChoice"
                        />
                    </div>
                </div>
            </div>

            <div class="columns">
                <div class="column" v-if="generatedBracket.length > 0">
                    <TableBracket ref="tableBracket" :edit-mode="true" :bracket-competitors="generatedBracket" />
                </div>
            </div>

            <AcademiesSummary ref="academiesSummary" :competitors="generatedBracket" />
        </div>
    </section>
</template>

<script setup lang="ts">
    import { ref, onMounted, nextTick, watch } from "vue";
    import TableBracket from "./TableBracket.vue";
    import BracketColorOptions from "./BracketColorOptions.vue";
    import AcademiesSummary from "./AcademiesSummary.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import BracketSeedingSelector from "./BracketSeedingSelector.vue";
    import BracketCategorySelect from "./BracketCategorySelect.vue";
    import { BracketSeedingTypeEnum, BracketSeedingType } from "../Common/types";
    import bracketsApi, { BracketTypeJsonView } from "../Api/brackets";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import { ComplexBracket } from "../Common/Brackets/types";
    import { BracketColorOption, BracketSeedCompetitor, BracketType } from "../Common/types";

    const props = defineProps<{
        eventId: number;
    }>();

    const selectedComplexBracket = ref<number | null>(null);
    const complexBracketsListing = ref<ComplexBracket[]>([]);
    const selectedSupportedBracketType = ref<BracketType>(0);
    const supportedBracketTypes = ref<BracketTypeJsonView[]>([]);
    const bracketTypeForCurrentSeeding = ref<BracketType>(0);
    const generatedBracket = ref<BracketSeedCompetitor[]>([]);
    const savingInProgress = ref<boolean>(false);
    const generatingInProgress = ref<boolean>(false);
    const selectedSeedingType = ref<BracketSeedingType>(BracketSeedingTypeEnum.ByRankingAndAcademy);
    const originalGeneratedOrder = ref<number[]>([]);

    const tableBracket = ref();
    const academiesSummary = ref();

    function openAcademiesSummary() {
        academiesSummary.value.open();
    }

    function fetchComplexBracketsListing() {
        return bracketsApi.getComplexBrackets(props.eventId, true).then((complexBrackets: ComplexBracket[]) => {
            complexBracketsListing.value = complexBrackets;
        });
    }

    function fetchBracketTypes() {
        generatedBracket.value = [];
        originalGeneratedOrder.value = [];
        // coloredAcademies is not used in this component, so omitted

        if (selectedComplexBracket.value === null) {
            selectedSupportedBracketType.value = 0;
            supportedBracketTypes.value = [];
            return;
        }

        return bracketsApi.getSupportedBracketTypes(props.eventId, selectedComplexBracket.value).then(response => {
            if (response.length > 0) {
                supportedBracketTypes.value = response;
                selectedSupportedBracketType.value = response[0].id;
            } else {
                selectedSupportedBracketType.value = 0;
                supportedBracketTypes.value = [];
            }
        });
    }

    function generateBracketSeeding() {
        const parameterId = selectedComplexBracket.value;
        if (parameterId === null) {
            alert(translate("admin_create_bracket.choose_category"));
            return;
        }
        assertValue(supportedBracketTypes.value.length > 0, translate("admin_create_bracket.no_competitors_in_category"));

        generatingInProgress.value = true;
        bracketTypeForCurrentSeeding.value = selectedSupportedBracketType.value;
        generatedBracket.value = [];
        originalGeneratedOrder.value = [];

        return bracketsApi
            .generateBracketSeeding(props.eventId, parameterId, selectedSupportedBracketType.value, selectedSeedingType.value)
            .then((response: BracketSeedCompetitor[]) => {
                generatedBracket.value = response;
                originalGeneratedOrder.value = response.map((c: BracketSeedCompetitor) => c.id);
                nextTick(() => {
                    tableBracket.value.clearColoring();
                });
            })
            .catch(handleError)
            .finally(() => {
                generatingInProgress.value = false;
            });
    }

    function saveBracket() {
        const parameterId = selectedComplexBracket.value;
        if (parameterId === null) {
            alert(translate("admin_create_bracket.choose_category"));
            return;
        }
        assertValue(selectedSupportedBracketType.value > 0, translate("admin_create_bracket.choose_bracket_type"));

        const competitors = tableBracket.value.getCompetitors();
        const currentIds = competitors.map((c: BracketSeedCompetitor) => c.id);
        const orderChanged = JSON.stringify(originalGeneratedOrder.value) !== JSON.stringify(currentIds);

        savingInProgress.value = true;
        return bracketsApi
            .persistBracket({
                eventId: props.eventId,
                parameterId: parameterId,
                type: selectedSupportedBracketType.value,
                competitors: currentIds,
                seedingType: selectedSeedingType.value,
                manualChange: orderChanged,
            })
            .then(() => fetchComplexBracketsListing())
            .then(() => {
                successToast(translate("admin_create_bracket.bracket_save"));
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = false;
            });
    }

    function assertValue(condition: boolean, errorMessage: string, throwError?: boolean) {
        if (condition) {
            return true;
        }
        alert(errorMessage);
        throwError = throwError === undefined ? true : !!throwError;
        if (throwError) {
            throw Error("Cannot save/generate bracket.");
        }
        return false;
    }

    function onClearColoring() {
        tableBracket.value.clearColoring();
    }

    function onColoringChoice(coloring: BracketColorOption) {
        tableBracket.value.setRowsColoring(coloring);
    }

    function onChangeBracketType() {
        if (generatedBracket.value.length === 0) {
            bracketTypeForCurrentSeeding.value = selectedSupportedBracketType.value;
            return;
        }

        if (generatedBracket.value.length > 0 && confirm(translate("admin_create_bracket.sure_to_change_type"))) {
            generateBracketSeeding();
        } else {
            selectedSupportedBracketType.value = bracketTypeForCurrentSeeding.value;
        }
    }

    watch(selectedComplexBracket, fetchBracketTypes);

    onMounted(() => {
        return fetchComplexBracketsListing();
    });
</script>

<style scoped>
    .lb-md {
        font-size: 13px;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s;
    }

    .fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
        opacity: 0;
    }
</style>
